import React, { useState, useCallback } from 'react';
import { Authenticator, Sidebar, Input, Button, Modal } from 'components';
import { SidebarProvider } from 'context/SidebarContext';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
    Campaigns,
    ErrorScreen,
    NotFoundScreen,
    LoginScreen,
    NewLoginScreen,
    LogoutScreen,
    CartScreen,
    CodCheckout,
    DashboardScreen,
    FaqScreen,
    PrivacyPolicy,
    ManualSetup,
    PlanScreen,
    StickyCartSettings,
    InvitationScreen,
    Affiliation,
    ReferralScreen,
    Integrations
} from 'screens';
import { withCampaignProvider } from 'context/CampaignContext';
import { isEmbedded } from 'lib/wrappers';
import styles from 'styles/page.module.scss';
import useReactRouter from 'use-react-router';
import classNames from 'lib/classNames';
import { API } from 'aws-amplify';
import TutorialModal from 'components/TutorialModal';
import TallyModal from 'components/TallyModal';
import SetupScreen from 'screens/SetupScreen';
import createApp from '@shopify/app-bridge';
import { Redirect } from '@shopify/app-bridge/actions';
import { SHOPIFY_API_KEY } from 'aws-exports';

function App() {
    return (
        <BrowserRouter>
            <ErrorScreen>
                <Routes />
            </ErrorScreen>
        </BrowserRouter>
    );
}

export default App;

const Routes = () => {
    const [voucher, setVoucher] = useState('');
    const [newPlanRedirect, setNewPlanRedirect] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
    window.showUpgradeModal = setIsUpgradeModalVisible;
    const { location } = useReactRouter();
    // const [collapsed, originalSetCollapsed] = useState(
    //   // JSON.parse(localStorage.getItem("sidebar_collapsed"))
    // );
    // const setCollapsed = (value) => {
    //   // localStorage.setItem("sidebar_collapsed", value);
    //   originalSetCollapsed(value);
    // };
    const [collapsed, setCollapsed] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [upgradeError, setUpgradeError] = useState(null);

    function upgrade() {
        if (!window.shop) {
            setUpgradeError('Shop not found. Please reload the page.');
            return;
        }
        setIsUpgrading(true);
        setUpgradeError(null);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://monster-worker.com/upgrade?shop=${window.shop}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function () {
            try {
                const data = JSON.parse(xhr.responseText);
                if (data.success) {
                    const params = new URLSearchParams(window.location.search);
                    const host = params.get('host') || btoa(window.shop);
                    const app = createApp({ apiKey: SHOPIFY_API_KEY, host });
                    const redirect = Redirect.create(app);
                    redirect.dispatch(Redirect.Action.ADMIN_PATH, {
                        path: '/apps/monster-upsells'
                    });
                } else {
                    setIsUpgrading(false);
                    setUpgradeError('Upgrade failed. Please try again.');
                }
            } catch (e) {
                setIsUpgrading(false);
                setUpgradeError('Unexpected error. Please try again.');
            }
        };
        xhr.onerror = function () {
            setIsUpgrading(false);
            setUpgradeError('Network error. Please check your connection.');
        };
        xhr.send(JSON.stringify({ shopName: window.shop }));
    }

    if (
        ['/login', '/auth/shopify/callback'].includes(location.pathname) ||
        new RegExp('/invitation/.*').test(location.pathname) ||
        new RegExp('/ref/.*').test(location.pathname)
    )
        return (
            <Switch>
                {/* <Route path="/login" component={LoginScreen} /> */}
                <Route path="/login" component={NewLoginScreen} />
                <Route path="/invitation/:id" component={InvitationScreen} />
                <Route path="/ref/:code" component={ReferralScreen} />
            </Switch>
        );
    else
        return (
            <Authenticator>
                <>
                    <Modal
                        visible={isUpgradeModalVisible}
                        onClose={() => setIsUpgradeModalVisible(false)}
                        style={{ minWidth: '900px' }}
                        title={
                            <div className={classNames(' font-bold flex-center text-2xl')}>
                                You’re about to upgrade to Monster Cart V2
                            </div>
                        }
                        footer={
                            <div>
                                {upgradeError && (
                                    <div className={classNames('mb-2 p-2 bg-red-100 text-red-700 text-sm rounded')}>
                                        {upgradeError}
                                    </div>
                                )}
                                <div className={`${classNames('flex justify-end')} `}>
                                    <a
                                        className={classNames(
                                            'py-2 px-5 text-sm cursor-pointer flex items-center'
                                        )}
                                        href="https://www.loom.com/share/bf5a0512b4cf4298a1006145b51566ae"
                                    >
                                        Learn more about Monster Cart 2.0
                                    </a>
                                    <a
                                        className={classNames(
                                            'py-2 px-5 font-bold rounded-sm text-white text-sm cursor-pointer flex items-center bg-black rounded',
                                            isUpgrading && 'opacity-50 pointer-events-none'
                                        )}
                                        onClick={!isUpgrading ? upgrade : undefined}
                                    >
                                        {isUpgrading ? 'Upgrading...' : 'Upgrade Now'}
                                    </a>
                                </div>
                            </div>
                        }
                    >
                        {(close, setTitle) => (
                            <div className={classNames('space-y-3 p-4')}>
                                <p>
                                    Our next-generation cart with better performance, flexibility,
                                    and new features.
                                </p>
                                <h2 className={classNames('font-bold')}>
                                    Before you continue (Recommended)
                                </h2>
                                <p>
                                    Cart V2 uses a new layout system. Most stores won’t see any
                                    changes, but some styling may look slightly different.
                                </p>
                                <p>
                                    We recommend reviewing your current cart and taking a screenshot
                                    for reference.
                                </p>
                                <h2 className={classNames('font-bold')}>
                                    What you’ll get with Cart V2
                                </h2>
                                <ul className={classNames('space-y-2')}>
                                    <li>
                                        🚀 Faster cart, checkout, and store performance (Increase
                                        Conversion rates)
                                    </li>
                                    <li>
                                        🤩 Integrated Shopify function discounts (Compatible with
                                        more apps and capabilities)
                                    </li>
                                    <li>
                                        🎨 More flexible upsell and layout options (Boost AOV Even
                                        More)
                                    </li>
                                    <li>
                                        🔧 Increased updates rollout speed (Roadmap is focused V2
                                        and moving fast)
                                    </li>
                                    <li>⬆️ Upgraded to new Shopify billing system</li>
                                </ul>
                            </div>
                        )}
                    </Modal>
                    <div
                        className={classNames(
                            'hidden w-full h-10 flex-center text-white font-bold bg-orange-500 space-x-2 p-1'
                        )}
                    >
                        <span>
                            Warning - Upgrade to Monster Cart V2 Now (Your current cart will soon be
                            deprecated)
                        </span>
                        <button
                            type="button"
                            className={classNames(
                                'border-none text-orange-500 bg-white py-1 px-2 rounded cursor-pointer font-bold'
                            )}
                            onClick={() => setIsUpgradeModalVisible(true)}
                        >
                            Upgrade Now
                        </button>
                    </div>
                    <div
                        className={classNames(
                            'hidden px-20 pr-8 mx-auto py-2 flex-center bg-indigo-800 text-white font-bold'
                        )}
                    >
                        <span className={classNames('flex-1')}>Have a voucher code?</span>
                        <div className={classNames('w-2/3 flex items-center justify-end')}>
                            <Input
                                value={voucher}
                                onChange={(value) => setVoucher(value)}
                                isFlex
                                placeholder="Voucher code"
                            />
                            <Button
                                text="Use It Now"
                                type="secondary2"
                                className={classNames('ml-3')}
                                onClick={async () => {
                                    const planResponse = await API.post(
                                        'API_GATEWAY_NEW',
                                        '/shop/get_plan',
                                        {
                                            body: {
                                                voucher
                                            }
                                        }
                                    );
                                    setVoucher('');
                                    setNewPlanRedirect(planResponse.confirmation_url);
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.app_wrapper} data-collapsed={collapsed}>
                        <SidebarProvider value={{ collapsed, setCollapsed }}>
                            <Sidebar />
                        </SidebarProvider>
                        <Switch>
                            <Route path="/logout" component={LogoutScreen} />
                            <Route
                                path="/"
                                component={isEmbedded({
                                    ChildComponent: SetupScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.list,
                                    previewOpenByDefault: false,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns/create"
                                component={isEmbedded({
                                    ChildComponent: withCampaignProvider(
                                        Campaigns.create,
                                        null,
                                        null,
                                        setIsOpen
                                    ),
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                                exact={true}
                            />
                            <Route
                                path="/campaigns/create/:id"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.duplicate,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                            />
                            <Route
                                path="/campaigns/:id"
                                component={isEmbedded({
                                    ChildComponent: Campaigns.update,
                                    newPlanRedirect,
                                    setIsOpen
                                })}
                            />
                            <Route
                                path="/settings"
                                component={isEmbedded({
                                    ChildComponent: CartScreen,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />

                            <Route
                                path="/sticky"
                                component={isEmbedded({
                                    ChildComponent: StickyCartSettings,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/settings/checkout"
                                component={isEmbedded({
                                    ChildComponent: CodCheckout,
                                    previewOpenByDefault: true,
                                    noCampaign: true,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/settings/setup"
                                component={isEmbedded({
                                    ChildComponent: ManualSetup,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/dashboard"
                                component={isEmbedded({
                                    ChildComponent: DashboardScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route
                                path="/plan"
                                component={isEmbedded({
                                    ChildComponent: PlanScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/integrations"
                                component={isEmbedded({
                                    ChildComponent: Integrations,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/faq"
                                component={isEmbedded({
                                    ChildComponent: FaqScreen,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />
                            <Route
                                path="/privacy_policy"
                                component={isEmbedded({
                                    ChildComponent: PrivacyPolicy,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                            />

                            <Route
                                path="/affiliation"
                                component={isEmbedded({
                                    ChildComponent: Affiliation,
                                    previewOpenByDefault: false,
                                    newPlanRedirect
                                })}
                                exact={true}
                            />
                            <Route component={NotFoundScreen} />
                        </Switch>
                        <TutorialModal isOpen={isOpen} setIsOpen={setIsOpen} />
                        <TallyModal />
                    </div>
                </>
            </Authenticator>
        );
};
